import { useRef, useCallback } from 'react';
import { apiService } from '../services/api';

const POLL_INTERVAL_MS = 3000;
const MAX_POLL_DURATION_MS = 5 * 60 * 1000;

interface WatchdogCallbacks {
  onSuccess: () => void;
  onFailed: () => void;
  onCanceled: () => void;
}

export function useWatchdog() {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stop = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const start = useCallback((sid: string, callbacks: WatchdogCallbacks) => {
    stop();

    timeoutRef.current = setTimeout(() => {
      stop();
      callbacks.onCanceled();
    }, MAX_POLL_DURATION_MS);

    intervalRef.current = setInterval(async () => {
      try {
        const response = await apiService.getHppStatus(sid);

        switch (response.status) {
          case 'success':
            stop();
            callbacks.onSuccess();
            break;
          case 'failed':
            stop();
            callbacks.onFailed();
            break;
          case 'canceled':
            stop();
            callbacks.onCanceled();
            break;
        }
      } catch (error) {
        console.error('Watchdog poll error:', error);
      }
    }, POLL_INTERVAL_MS);
  }, [stop]);

  return { start, stop };
}
